import { type EntitiesVerbTypesConfig, genXAPIValidData } from '..'
import { CONFIG_ELEMENTS } from '@vedalib/editor/lib/elements'

type IAnalyticElConfigVariables<
  T extends keyof EntitiesVerbTypesConfig,
  V extends keyof EntitiesVerbTypesConfig[T] = keyof EntitiesVerbTypesConfig[T],
  E extends string | undefined = string,
> = {
  resultExtensions: EntitiesVerbTypesConfig[T][V] extends { resultExtensions: infer R } ? R : never
  objectExtensions: EntitiesVerbTypesConfig[T][V] extends { objectExtensions: infer O }
    ? O
    : Record<string, unknown> | undefined
} & (E extends undefined ? Record<string, unknown> : { elementId: E })

export const entityAnalyticsConfig = {
  [CONFIG_ELEMENTS.accordion.type]: {
    expanded: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.accordion.type, 'expanded'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.accordion.type,
        verb: 'expanded',
        data: {
          elementType: CONFIG_ELEMENTS.accordion.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
    collapsed: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.accordion.type, 'collapsed'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.accordion.type,
        verb: 'collapsed',
        data: {
          elementType: CONFIG_ELEMENTS.accordion.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.video.type]: {
    played: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.video.type, 'played'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.video.type,
        verb: 'played',
        data: {
          elementType: CONFIG_ELEMENTS.video.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
    paused: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.video.type, 'paused'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.video.type,
        verb: 'paused',
        data: {
          elementType: CONFIG_ELEMENTS.video.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
    updated: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.video.type, 'updated'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.video.type,
        verb: 'updated',
        data: {
          elementType: CONFIG_ELEMENTS.video.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.audio.type]: {
    played: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.audio.type, 'played'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.audio.type,
        verb: 'played',
        data: {
          elementType: CONFIG_ELEMENTS.audio.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
    paused: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.audio.type, 'paused'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.audio.type,
        verb: 'paused',
        data: {
          elementType: CONFIG_ELEMENTS.audio.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
    updated: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.audio.type, 'updated'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.audio.type,
        verb: 'updated',
        data: {
          elementType: CONFIG_ELEMENTS.audio.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
    completed: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.audio.type, 'completed'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.audio.type,
        verb: 'completed',
        data: {
          elementType: CONFIG_ELEMENTS.audio.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.labeledGraphic.type]: {
    opened: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.labeledGraphic.type, 'opened'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.labeledGraphic.type,
        verb: 'opened',
        data: {
          elementType: CONFIG_ELEMENTS.labeledGraphic.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
    closed: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.labeledGraphic.type, 'closed'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.labeledGraphic.type,
        verb: 'closed',
        data: {
          elementType: CONFIG_ELEMENTS.labeledGraphic.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
    switch: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.labeledGraphic.type, 'switch'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.labeledGraphic.type,
        verb: 'switch',
        data: {
          elementType: CONFIG_ELEMENTS.labeledGraphic.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.image.type]: {
    zoomStarted: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.image.type, 'zoomStarted'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.image.type,
        verb: 'zoomStarted',
        data: {
          elementType: CONFIG_ELEMENTS.image.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
    zoomEnded: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.image.type, 'zoomEnded'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.image.type,
        verb: 'zoomEnded',
        data: {
          elementType: CONFIG_ELEMENTS.image.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.code.type]: {
    copy: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.code.type, 'copy'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.code.type,
        verb: 'copy',
        data: {
          elementType: CONFIG_ELEMENTS.code.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.splitter.type]: {
    interacted: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.splitter.type, 'interacted'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.splitter.type,
        verb: 'interacted',
        data: {
          elementType: CONFIG_ELEMENTS.splitter.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.downloader.type]: {
    interacted: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.downloader.type, 'interacted'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.downloader.type,
        verb: 'interacted',
        data: {
          elementType: CONFIG_ELEMENTS.downloader.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.link.type]: {
    interacted: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.link.type, 'interacted'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.link.type,
        verb: 'interacted',
        data: {
          elementType: CONFIG_ELEMENTS.link.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.card.type]: {
    flipped: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.card.type, 'flipped'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.card.type,
        verb: 'flipped',
        data: {
          elementType: CONFIG_ELEMENTS.card.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.checkbox.type]: {
    checked: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.checkbox.type, 'checked'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.checkbox.type,
        verb: 'checked',
        data: {
          elementType: CONFIG_ELEMENTS.checkbox.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.tabs.type]: {
    selected: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.tabs.type, 'selected'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.tabs.type,
        verb: 'selected',
        data: {
          elementType: CONFIG_ELEMENTS.tabs.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.grouping.type]: {
    interacted: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.grouping.type, 'interacted'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.grouping.type,
        verb: 'interacted',
        data: {
          elementType: CONFIG_ELEMENTS.grouping.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.slider.type]: {
    Interacted: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.slider.type, 'Interacted'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.slider.type,
        verb: 'Interacted',
        data: {
          elementType: CONFIG_ELEMENTS.slider.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.simulator.type]: {
    interacted: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.simulator.type, 'interacted'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.simulator.type,
        verb: 'interacted',
        data: {
          elementType: CONFIG_ELEMENTS.simulator.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.courseStructure.type]: {
    interacted: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.courseStructure.type, 'interacted'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.courseStructure.type,
        verb: 'interacted',
        data: {
          elementType: CONFIG_ELEMENTS.courseStructure.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.testMultipleChoice.type]: {
    interacted: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<
      typeof CONFIG_ELEMENTS.testMultipleChoice.type,
      'interacted'
    >) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.testMultipleChoice.type,
        verb: 'interacted',
        data: {
          elementType: CONFIG_ELEMENTS.testMultipleChoice.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.testSingleChoice.type]: {
    interacted: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.testSingleChoice.type, 'interacted'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.testSingleChoice.type,
        verb: 'interacted',
        data: {
          elementType: CONFIG_ELEMENTS.testSingleChoice.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.testKeyboardInput.type]: {
    interacted: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.testKeyboardInput.type, 'interacted'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.testKeyboardInput.type,
        verb: 'interacted',
        data: {
          elementType: CONFIG_ELEMENTS.testKeyboardInput.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.testMatching.type]: {
    interacted: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.testMatching.type, 'interacted'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.testMatching.type,
        verb: 'interacted',
        data: {
          elementType: CONFIG_ELEMENTS.testMatching.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.testRanking.type]: {
    interacted: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.testRanking.type, 'interacted'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.testRanking.type,
        verb: 'interacted',
        data: {
          elementType: CONFIG_ELEMENTS.testRanking.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.testHotspot.type]: {
    interacted: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.testHotspot.type, 'interacted'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.testHotspot.type,
        verb: 'interacted',
        data: {
          elementType: CONFIG_ELEMENTS.testHotspot.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.testGrouping.type]: {
    interacted: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.testGrouping.type, 'interacted'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.testGrouping.type,
        verb: 'interacted',
        data: {
          elementType: CONFIG_ELEMENTS.testGrouping.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.button.type]: {
    interacted: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.button.type, 'interacted'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.button.type,
        verb: 'interacted',
        data: {
          elementType: CONFIG_ELEMENTS.button.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  [CONFIG_ELEMENTS.reflection.type]: {
    interacted: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<typeof CONFIG_ELEMENTS.reflection.type, 'interacted'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: CONFIG_ELEMENTS.reflection.type,
        verb: 'interacted',
        data: {
          elementType: CONFIG_ELEMENTS.reflection.type,
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  courseStructureCourse: {
    interacted: ({
      resultExtensions,
      elementId,
      objectExtensions,
    }: IAnalyticElConfigVariables<'courseStructure', 'interacted'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: 'courseStructure',
        verb: 'interacted',
        data: {
          elementType: 'courseStructure',
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  courseInitialized: {
    initialized: ({
      resultExtensions,
      elementId,
      objectExtensions,
    }: IAnalyticElConfigVariables<'initialized', 'initialized'>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: 'initialized',
        verb: 'initialized',
        data: {
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  courseStart: {
    started: ({
      resultExtensions,
      objectExtensions,
    }: IAnalyticElConfigVariables<'started', 'started', undefined>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: 'started',
        verb: 'started',
        data: {
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  courseQuestionValidate: {
    answer: ({
      resultExtensions,
      objectExtensions,
    }: IAnalyticElConfigVariables<'answer', 'answer', undefined>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: 'answer',
        verb: 'answer',
        data: {
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  courseCompleted: {
    completed: ({
      resultExtensions,
      objectExtensions,
    }: IAnalyticElConfigVariables<'completed', 'completed', undefined>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: 'completed',
        verb: 'completed',
        data: {
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  coursePassed: {
    passed: ({
      resultExtensions,
      objectExtensions,
    }: IAnalyticElConfigVariables<'passed', 'passed', undefined>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: 'passed',
        verb: 'passed',
        data: {
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  courseFailed: {
    failed: ({
      resultExtensions,
      objectExtensions,
    }: IAnalyticElConfigVariables<'failed', 'failed', undefined>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: 'failed',
        verb: 'failed',
        data: {
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  courseAttempted: {
    attempted: ({
      resultExtensions,
      objectExtensions,
    }: IAnalyticElConfigVariables<'attempted', 'attempted', undefined>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: 'attempted',
        verb: 'attempted',
        data: {
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  courseEnd: {
    terminated: ({
      resultExtensions,
      objectExtensions,
    }: IAnalyticElConfigVariables<'terminated', 'terminated', undefined>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: 'terminated',
        verb: 'terminated',
        data: {
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  enteredSection: {
    enteredSection: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<'enteredSection', 'enteredSection', string>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: 'enteredSection',
        verb: 'enteredSection',
        data: {
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  exitedSection: {
    exitedSection: ({
      resultExtensions,
      objectExtensions,
      elementId,
    }: IAnalyticElConfigVariables<'exitedSection', 'exitedSection', string>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: 'exitedSection',
        verb: 'exitedSection',
        data: {
          elementId,
          resultExtensions,
          objectExtensions,
        },
      })
      return xAPIData
    },
  },
  enteredBlock: {
    enteredBlock: ({
      resultExtensions,
      objectExtensions,
    }: IAnalyticElConfigVariables<'enteredBlock', 'enteredBlock', string>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: 'enteredBlock',
        verb: 'enteredBlock',
        data: { resultExtensions, objectExtensions },
      })
      return xAPIData
    },
  },
  exitedBlock: {
    exitedBlock: ({
      resultExtensions,
      objectExtensions,
    }: IAnalyticElConfigVariables<'exitedBlock', 'exitedBlock', string>) => {
      const { xAPIData } = genXAPIValidData({
        elementType: 'exitedBlock',
        verb: 'exitedBlock',
        data: { resultExtensions, objectExtensions },
      })
      return xAPIData
    },
  },
}
